import { useState } from "react";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { PageHeader, Field, CopyButton, inputCls } from "@/components/shared";

const OUTPUT_TYPES = [
  ["viewing_report", "viewingReport"],
  ["seller_followup", "sellerFollowUp"],
  ["buyer_followup", "buyerFollowUp"],
  ["property_advert", "propertyAdvert"],
  ["translate_fr", "frenchTranslation"],
  ["translate_en", "englishTranslation"],
  ["whatsapp", "whatsappMessage"],
  ["formal_email", "formalEmail"],
];

export default function AIGenerator() {
  const [notes, setNotes] = useState("");
  const [outputType, setOutputType] = useState("viewing_report");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const { t } = useLang();

  const generate = async () => {
    if (!notes.trim()) return toast.error(t("required"));
    setLoading(true);
    setOutput("");
    try {
      const res = await fetch(`${API}/ai/generate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notes, output_type: outputType }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          try {
            const json = JSON.parse(part.slice(6));
            if (json.delta) setOutput((prev) => prev + json.delta);
            if (json.error) toast.error(json.error);
          } catch (parseErr) {
            if (process.env.NODE_ENV === "development") {
              console.warn("Skipped malformed SSE chunk", parseErr);
            }
          }
        }
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="ai-generator-page">
      <PageHeader title={t("aiTitle")} subtitle={t("aiSubtitle")} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-stone-200 rounded-xl p-6 space-y-5">
          <Field label={t("outputType")}>
            <Select value={outputType} onValueChange={setOutputType}>
              <SelectTrigger data-testid="ai-output-type-select" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent>
                {OUTPUT_TYPES.map(([value, key]) => (
                  <SelectItem key={value} value={value} data-testid={`ai-type-${value}`}>{t(key)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("roughNotes")}>
            <textarea
              data-testid="ai-notes-input"
              rows={12}
              className={inputCls}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. thompsons visited eymet farmhouse, loved beams + barn, worried about fosse, deciding thursday..."
            />
          </Field>
          <button
            data-testid="ai-generate-button"
            onClick={generate}
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#2F4F4F] hover:bg-[#243c3c] text-white rounded-lg px-6 py-3 font-medium transition-colors disabled:opacity-60">
            <Sparkles size={16} strokeWidth={1.5} />
            {loading ? t("generating") : t("generate")}
          </button>
        </div>
        <div className="bg-white border border-stone-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">{t("result")}</h3>
            {output && <CopyButton text={output} testId="copy-ai-output-button" />}
          </div>
          {output ? (
            <pre data-testid="ai-output-content" className="whitespace-pre-wrap font-body text-sm text-stone-700 leading-relaxed">
              {output}
              {loading && <span className="inline-block w-2 h-4 bg-[#2F4F4F] ml-0.5 animate-pulse" />}
            </pre>
          ) : (
            <div className="text-center py-20 text-stone-300">
              <Sparkles size={32} strokeWidth={1} className="mx-auto mb-3" />
              <p className="text-sm">{loading ? t("generating") : "…"}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
